/**
 * Build-time configuration, from Vite's `import.meta.env`.
 *
 * Anything read here is **baked into the bundle and shipped to every visitor**.
 * That is not a leak to be fixed later; it is what `VITE_`-prefixed variables
 * are for, and Vite refuses to expose anything without the prefix precisely so
 * the boundary is explicit. A real secret must never appear in this file.
 */

/**
 * The Google OAuth client id the SPA signs in with.
 *
 * **This is not a secret, and it is not supposed to be one.** It is the public
 * OAuth *audience*: the browser has to send it to Google in the clear for the
 * handshake to work at all, so it is visible in devtools on any site that uses
 * Google sign-in, including Google's own documentation. It reads like a leaked
 * credential — a long opaque string ending in `.apps.googleusercontent.com` —
 * so this note exists to stop a well-meaning future reader "fixing" it into
 * Secrets Manager and breaking sign-in.
 *
 * What actually protects the audience is the authorized-origins list on the
 * OAuth client itself: a token minted for this client id is only issued to
 * pages Google has been told to trust, and the API re-checks the `azp` claim
 * against the same id (see apps/api/src/auth/identity.ts). There is no client
 * *secret* anywhere in this design — GIS is a browser-side ID-token flow, so
 * there is nothing for a server to exchange.
 *
 * The API reads the same value as `GOOGLE_CLIENT_ID`; the two must match or
 * every token this app mints will fail audience verification.
 */
export const GOOGLE_CLIENT_ID: string = import.meta.env.VITE_GOOGLE_CLIENT_ID ?? '';

/**
 * Where the API lives. Defaults to a same-origin `/api`, which is what a
 * CloudFront distribution routing both the SPA and the API gives you (§7) —
 * and which sidesteps CORS entirely in production.
 */
export const API_BASE_URL: string = import.meta.env.VITE_API_BASE_URL ?? '/api';

/**
 * Sign-in cannot work without a client id, and the failure is otherwise
 * baffling: GIS logs an obscure origin error to the console and no button ever
 * appears. Surfacing it as a first-class state lets the sign-in screen say what
 * is actually wrong (see SignIn.tsx).
 */
export const isAuthConfigured = (): boolean => GOOGLE_CLIENT_ID.length > 0;
